/**
 * @file Auras des autres joueurs du hub : petit lot d'AuraRenderer / AuraRig reutilises, attribues aux joueurs distants les plus proches
 *       du chef (cout borne : MAX_REMOTE_AURAS emplacements, aucun en mode eco). Attribution stable : un joueur garde son emplacement
 *       tant qu'il reste parmi les plus proches.
 */
import type { PerspectiveCamera, Scene } from 'three';
import type { AuraStyle } from '@config/types';
import type { ToonKit } from '../toon/toon';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import { AuraRenderer } from './AuraRenderer';
import { AuraRigRenderer } from './AuraRig';

/** Emplacements d'aura distante (les plus proches du chef). */
export const MAX_REMOTE_AURAS = 4;

export interface RemoteAuraEntry {
  id: string;
  /** Position monde Three (x, z). */
  x: number;
  z: number;
  style: AuraStyle;
}

interface Slot {
  id: string | null;
  aura: AuraRenderer;
  rig: AuraRigRenderer | null;
}

export class RemoteAuras {
  private readonly slots: Slot[] = [];
  private eco = false;

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {}

  setEco(eco: boolean): void {
    this.eco = eco;
    for (const s of this.slots) {
      s.aura.setEco(eco);
      s.rig?.setEco(eco);
    }
  }

  /** Une frame. `entries` = joueurs distants portant une aura ; (cx, cz) = chef (tri par distance). Liste vide = tout s'eteint en fondu. */
  update(entries: readonly RemoteAuraEntry[], cx: number, cz: number, time: number, dt: number, camera: PerspectiveCamera): void {
    const wanted = this.eco ? [] : [...entries].sort((a, b) => (a.x - cx) ** 2 + (a.z - cz) ** 2 - ((b.x - cx) ** 2 + (b.z - cz) ** 2)).slice(0, MAX_REMOTE_AURAS);
    const byId = new Map(wanted.map((e) => [e.id, e]));
    // garder les emplacements deja attribues, liberer les autres
    for (const s of this.slots) if (s.id !== null && !byId.has(s.id)) s.id = null;
    const placed = new Set(this.slots.map((s) => s.id).filter((id): id is string => id !== null));
    for (const e of wanted) {
      if (placed.has(e.id)) continue;
      let slot = this.slots.find((s) => s.id === null);
      if (!slot && this.slots.length < MAX_REMOTE_AURAS) {
        const aura = new AuraRenderer(this.scene, this.kit, this.geo);
        aura.setEco(this.eco);
        slot = { id: null, aura, rig: null };
        this.slots.push(slot);
      }
      if (!slot) break;
      slot.id = e.id;
    }
    const q = camera.quaternion;
    for (const s of this.slots) {
      const e = s.id !== null ? byId.get(s.id) : undefined;
      const style = e?.style ?? null;
      const x = e?.x ?? 0;
      const z = e?.z ?? 0;
      if (style?.rig && !s.rig) {
        s.rig = new AuraRigRenderer(this.scene);
        s.rig.setEco(this.eco);
      }
      s.aura.update(style?.rig ? null : style, x, z, time, dt, q);
      s.rig?.update(style?.rig ? style : null, x, z, dt, camera);
    }
  }

  dispose(): void {
    for (const s of this.slots) {
      s.aura.dispose();
      s.rig?.dispose();
    }
    this.slots.length = 0;
  }
}
